import { FileText, Printer } from 'lucide-react';
import { toast } from 'sonner';

import api from '../../lib/api';
import { fmtCurrency } from '../../lib/localeUtils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

// Signature slip for the counter — A4 sheet or 80mm till roll.
export const downloadSlip = async (handoverId, fmt) => {
  try {
    const { data } = await api.get(`/shift/handovers/${handoverId}/slip`, {
      params: { fmt }, responseType: 'blob',
    });
    const url = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `handover_${handoverId}_${fmt}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error('Could not build the handover slip');
  }
};

export const VarianceBadge = ({ handover }) => {
  const variance = Number(handover.variance || 0);
  const within = Math.abs(variance) <= Number(handover.variance_threshold ?? 0);
  const tone = within
    ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30'
    : 'bg-amber-500/10 text-amber-600 border-amber-500/30';
  return (
    <Badge variant="outline" className={`text-xs ${tone}`} data-testid={`handover-variance-${handover.handover_id}`}>
      {variance === 0 ? 'Balanced' : `${variance > 0 ? 'Over' : 'Short'} ${fmtCurrency(Math.abs(variance))}`}
    </Badge>
  );
};

export const HandoverRowDetail = ({ handover }) => (
  <div className="grid sm:grid-cols-3 gap-4 py-2 text-sm" data-testid={`handover-detail-${handover.handover_id}`}>
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">By payment</p>      {(handover.payments || []).length === 0 ? <p className="text-muted-foreground">No sales</p>
        : handover.payments.map((p) => (
          <div key={p.method} className="flex justify-between gap-4">
            <span>{p.label} ({p.count})</span>
            <span className="tabular-nums">{fmtCurrency(p.total)}</span>
          </div>
        ))}
      <div className="flex justify-between gap-4 text-muted-foreground mt-1">
        <span>Refunds</span><span className="tabular-nums">− {fmtCurrency(handover.refunds_total)}</span>
      </div>
    </div>
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Drawer count</p>
      {(handover.denominations || []).length === 0 ? <p className="text-muted-foreground">Total entered directly</p>
        : handover.denominations.map((d) => (
          <div key={d.value} className="flex justify-between gap-4">
            <span>{d.value} × {d.count}</span>
            <span className="tabular-nums">{fmtCurrency(d.value * d.count)}</span>
          </div>
        ))}
    </div>
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Shift</p>
      <p className="text-muted-foreground">Started {new Date(handover.shift_start).toLocaleString()}</p>
      <p className="text-muted-foreground">Opening float {fmtCurrency(handover.opening_float || 0)}</p>
      {handover.note && <p className="mt-2">“{handover.note}”</p>}
      <div className="flex gap-2 mt-3">
        <Button size="sm" variant="outline" className="gap-1.5 h-8"
          onClick={() => downloadSlip(handover.handover_id, 'a4')}
          data-testid={`handover-slip-a4-${handover.handover_id}`}>
          <FileText className="h-3.5 w-3.5" /> A4 slip
        </Button>
        <Button size="sm" variant="outline" className="gap-1.5 h-8"
          onClick={() => downloadSlip(handover.handover_id, 'till')}
          data-testid={`handover-slip-till-${handover.handover_id}`}>
          <Printer className="h-3.5 w-3.5" /> Till roll
        </Button>
      </div>
    </div>
  </div>
);
